// src/utils/errorUtils.js

// Error boundary component
import React from 'react';

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export const errorMessages = {
  NETWORK_ERROR: 'Network connection failed. Please check your internet connection.',
  UNAUTHORIZED: 'Your session has expired. Please login again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

export const getErrorMessage = (error) => {
  // Handle different types of errors
  if (error instanceof ApiError) {
    return error.message;
  }
  
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return errorMessages.NETWORK_ERROR;
  }
  
  if (error.message.includes('401') || error.message.includes('Unauthorized')) {
    return errorMessages.UNAUTHORIZED;
  }
  
  if (error.message.includes('403') || error.message.includes('Forbidden')) {
    return errorMessages.FORBIDDEN;
  }
  
  if (error.message.includes('404') || error.message.includes('Not Found')) {
    return errorMessages.NOT_FOUND;
  }
  
  if (error.message.includes('500') || error.message.includes('Server Error')) {
    return errorMessages.SERVER_ERROR;
  }
  
  if (error.message.includes('timeout')) {
    return errorMessages.TIMEOUT_ERROR;
  }
  
  // Return the original error message if it's user-friendly
  if (error.message && error.message.length < 100) {
    return error.message;
  }
  
  return errorMessages.UNKNOWN_ERROR;
};

export const handleApiError = (error, showToast = null) => {
  const message = getErrorMessage(error);
  
  // Log error for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.error('API Error:', error);
  }
  
  // Show toast notification if function provided
  if (showToast && typeof showToast === 'function') {
    showToast(message, 'error');
  }
  
  return message;
};

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                Something went wrong
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                An unexpected error occurred. Please refresh the page and try again.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}